import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/components/ui/tooltip';
import withRouter from '../../../components/withRouter';
import TableContainer from '../../../components/TableContainerReactTable';
import AddEditUserModal from './AddEditUserModal';
import { getUsers } from '../../(identity)/services/userApi';
import { authClient } from '../../../lib/auth-client';
import { toast } from 'react-toastify';
import { telefoneMask } from '../../../utils';
import { User, UserRole } from '../../(identity)/types/user';
import { useProfile } from '../../../hooks/UserHooks';

const Index = (props: any) => {
    const { userProfile } = useProfile();
    const [data, setData] = useState<User[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [dataToEdit, setDataToEdit] = useState<User | null>(null);
    const [isAddEditModalOpen, setIsAddModalOpen] = useState<boolean>(false);

    useEffect(() => {
        getUsers()
            .then((res) => {
                setData(res);
            })
            .catch(() => {})
            .finally(() => {
                setLoading(false);
            });
    }, []);

    const triggerImpersonateUser = async (id: number) => {
        try {
            await authClient.admin.impersonateUser({
                userId: id.toString(),
            });
            window.location.replace('/campaign');
        } catch {
            toast.error('Erro ao personificar usuário');
        }
    };

    const handleUpdateData = (newData: User) => {
        const updatedData = data.map((item) => {
            if (item.id === newData.id) {
                return newData;
            }
            return item;
        });
        setData(updatedData);
    };

    const handleAddData = (newData: User) => {
        setData([...data, newData]);
    };

    const toggleAddEditModal = () => {
        setIsAddModalOpen(!isAddEditModalOpen);
    };

    const columns = [
        {
            header: '#',
            cell: (cell: any) => {
                return <span className="fw-semibold">{cell.getValue()}</span>;
            },
            accessorKey: 'id',
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: 'Nome',
            accessorKey: 'name',
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: 'Empresa',
            accessorFn: (row: any) => {
                return row.publisher?.name ?? row.agency?.name ?? row.advertiser?.name ?? '-';
            },
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: 'Email',
            accessorKey: 'email',
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: 'Telefone',
            accessorFn: (row: any) => {
                return row.phone ? telefoneMask(row.phone) : '-';
            },
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: 'Cargo',
            accessorFn: (row: any) => {
                return row.jobTitle ?? '-';
            },
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: 'Permissão',
            accessorFn: (row: any) => {
                return UserRole[row.role as keyof typeof UserRole] ?? '-';
            },
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: 'Tipo',
            accessorFn: (row: any) => {
                return row.publisher ? 'Publisher' : row.agency ? 'Agência' : row.advertiser ? 'Anunciante' : 'Ad Pulse';
            },
            cell: (cell: any) => {
                const row = cell.row.original;
                const badgeColor = row.publisher ? 'primary' : row.agency ? 'secondary' : row.advertiser ? 'success' : 'info';
                return <span className={`badge bg-${badgeColor}`}>{cell.getValue()}</span>;
            },
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: 'Status',
            accessorKey: 'status',
            cell: (cell: any) => {
                const row = cell.row.original;
                if (row.status) {
                    return <span className="badge bg-success-subtle text-success">ATIVO</span>;
                }

                return <span className="badge bg-danger-subtle text-danger">DESATIVADO</span>;
            },
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            id: 'actions',
            cell: (cell: any) => {
                const row = cell.row.original;
                const actionCell = [];

                actionCell.push(
                    <TooltipProvider key="action_edit">
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <button
                                    className="btn text-primary d-inline-block"
                                    onClick={() => {
                                        setDataToEdit(row);
                                        toggleAddEditModal();
                                    }}
                                >
                                    <i className="ri-pencil-fill fs-16"></i>
                                </button>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>Editar</p>
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>,
                );

                if (userProfile && !userProfile.impersonatedUser && userProfile.role === 'ROLE_SUPER_ADMIN' && row.id !== userProfile.id) {
                    actionCell.push(
                        <TooltipProvider key="action_impersonate">
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <button
                                        className="btn text-primary d-inline-block"
                                        onClick={() => {
                                            triggerImpersonateUser(row.id);
                                        }}
                                    >
                                        <i className="ri-user-shared-fill fs-16"></i>
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent>
                                    <p>Personificar</p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>,
                    );
                }
                return <div className="d-flex gap-2">{actionCell}</div>;
            },
            enableColumnFilter: false,
            enableSorting: false,
        },
    ];

    document.title = 'Usuários | Ad Pulse';
    return (
        <React.Fragment>
            <div className="page-content">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-lg-12">
                            <Card>
                                <CardHeader>
                                    <div className="row g-4 align-items-center">
                                        <div className="col-sm">
                                            <h5 className="card-title mb-0">Usuários</h5>
                                        </div>
                                        <div className="col-sm-auto">
                                            <Button
                                                onClick={() => {
                                                    setDataToEdit(null);
                                                    toggleAddEditModal();
                                                }}
                                            >
                                                <i className="ri-add-line align-bottom me-1"></i> Adicionar usuário
                                            </Button>
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    {!loading ? (
                                        <TableContainer
                                            divClass="table-responsive"
                                            columns={columns || []}
                                            data={data || []}
                                            isGlobalFilter={true}
                                            customPageSize={30}
                                            SearchPlaceholder="Procurar..."
                                        />
                                    ) : (
                                        <div className="text-center">
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="visually-hidden">Carregando...</span>
                                            </div>
                                        </div>
                                    )}

                                    <AddEditUserModal
                                        user={dataToEdit}
                                        isOpen={isAddEditModalOpen}
                                        toggle={toggleAddEditModal}
                                        onAddData={handleAddData}
                                        onUpdateData={handleUpdateData}
                                    />
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
};

export default withRouter(Index);
